/**
 * MyToolbar Component
 *
 * Custom form toolbar with responsive Save and Cancel buttons.
 * Features:
 * - Sticky positioning on mobile for better UX
 * - Responsive button sizes
 * - Optional delete button
 * - Consistent styling across the app
 *
 * USAGE:
 * ```tsx
 * <SimpleForm toolbar={<MyToolbar resource="examples" />}>
 *   ...
 * </SimpleForm>
 * ```
 */

import React from 'react';
import {
  Toolbar,
  SaveButton,
  DeleteButton,
  useTranslate,
  useRedirect,
  ToolbarProps,
} from 'react-admin';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';

interface MyToolbarProps extends ToolbarProps {
  /** Custom cancel handler */
  onCancel?: () => void;
  /** Resource name for redirect on cancel */
  resource?: string;
  /** Show delete button (default: false) */
  showDelete?: boolean;
  /** Custom delete handler */
  onDelete?: () => void;
  /** Alias for showDelete */
  deleteButton?: boolean;
  /**
   * Keep Save enabled on an untouched form. Needed when the form arrives
   * already filled in (converting a sign-up request opens the client creation
   * pre-filled), where the default pristine-form guard would otherwise force
   * the operator to edit a field before being allowed to save.
   */
  alwaysEnableSave?: boolean;
}

const MyToolbar: React.FC<MyToolbarProps> = ({
  onCancel,
  resource = '',
  showDelete = false,
  onDelete,
  deleteButton = false,
  alwaysEnableSave = false,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translate = useTranslate();
  const redirect = useRedirect();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      redirect('list', resource);
    }
  };

  return (
    <Toolbar
      {...rest}
      sx={{
        marginTop: 'auto',
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 2 : 3,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 1100,
        // Fixed position on mobile for always-visible buttons
        ...(isMobile && {
          position: 'fixed',
          left: 0,
          right: 0,
          boxShadow: `0 -4px 12px ${theme.palette.action.hover}`,
        }),
      }}
    >
      {/* Left side - Delete button (optional) */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {(showDelete || deleteButton) && (
          <>
            {onDelete ? (
              <Button
                variant="outlined"
                color="error"
                onClick={onDelete}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: isMobile ? 80 : 100,
                  ...(isMobile && { fontSize: '0.875rem' }),
                }}
              >
                {translate('ra.action.delete')}
              </Button>
            ) : (
              <DeleteButton
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  ...(isMobile && { fontSize: '0.875rem' }),
                }}
              />
            )}
          </>
        )}
      </Box>

      {/* Right side - Cancel and Save buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* Cancel button */}
        <Button
          variant={isMobile ? 'text' : 'outlined'}
          startIcon={!isMobile && <CloseIcon />}
          onClick={handleCancel}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            minWidth: isMobile ? 80 : 100,
            ...(isMobile && { fontSize: '0.875rem' }),
          }}
        >
          {translate('ra.action.cancel')}
        </Button>

        {/* Save button */}
        <SaveButton
          alwaysEnable={alwaysEnableSave}
          variant="contained"
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          icon={<SaveIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: isMobile ? 80 : 120,
            ...(isMobile && { fontSize: '0.875rem' }),
          }}
        />
      </Box>
    </Toolbar>
  );
};

export default MyToolbar;
